import { Company, Id } from '@nextforce/api'
import WheelPicker from '@quidone/react-native-wheel-picker'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  DropdownCompanyItemComponent,
  getCompanySections,
} from 'company/dropdown'
import React, { useCallback, useMemo, useState } from 'react'
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  SectionList,
  TouchableOpacity,
  View,
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Dropdown } from 'shared/components/dropdown'
import { useQuarterlyReports } from '../queries/list'
interface PerformanceReportViewProps {
  companies: Company[]
  activeCompanyId: string
  onCompanySelected: (id: string) => void
  onPressReportUrl: (url: string) => void
}

type ReportSection = {
  quarter: number
  data: Array<{
    type: 'team' | 'store'
  }>
}

const quarters = [1, 2, 3, 4] as const

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 10 }, (_, i) => currentYear - i) // range of 10 years

export function PerformanceReportView({
  companies,
  activeCompanyId,
  onCompanySelected,
  onPressReportUrl,
}: PerformanceReportViewProps) {
  const { theme } = useTheme()
  const styles = useStyles()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [isYearPickerVisible, setYearPickerVisible] = useState(false)
  const activeCompany = useMemo(() => {
    return companies.find(company => company.id === activeCompanyId)
  }, [companies, activeCompanyId])

  const handleCompanySelect = useCallback(
    (id: Id) => {
      if (onCompanySelected) {
        onCompanySelected(id)
      }
    },
    [onCompanySelected]
  )

  const companySections = useMemo(() => {
    if (!companies) return []
    return getCompanySections(companies, activeCompanyId)
  }, [companies, activeCompanyId])

  const sections: ReportSection[] = useMemo(
    () =>
      quarters.map(quarter => ({
        quarter,
        data: [{ type: 'team' }, { type: 'store' }],
      })),
    []
  )

  const renderSectionHeader = ({ section }: { section: ReportSection }) => (
    <View style={styles.sectionHeader}>
      <Text h4>{`Q${section.quarter} ${selectedYear}`}</Text>
    </View>
  )

  const renderItem = useCallback(
    ({
      item,
      section,
    }: {
      item: ReportSection['data'][0]
      section: ReportSection
    }) => (
      <PerformanceReportListItem
        companyId={activeCompanyId}
        quarter={section.quarter}
        year={selectedYear}
        type={item.type}
        onPressReportUrl={onPressReportUrl}
      />
    ),
    [activeCompanyId, selectedYear, onPressReportUrl]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={styles.companySelector}>
          <Dropdown
            sections={companySections}
            selectedId={activeCompanyId}
            onSelect={handleCompanySelect}
            title={activeCompany?.name}
            ItemComponent={DropdownCompanyItemComponent}
            titleStyle={styles.companySelectorTitle}
          />
        </View>
        <TouchableOpacity
          style={styles.yearSelector}
          onPress={() => setYearPickerVisible(true)}
        >
          <View style={styles.yearSelectorContainer}>
            <Text h4>{`${t('year')} : ${selectedYear}`}</Text>
            <Icon
              name="caret-down"
              size={13}
              type="font-awesome-5"
              solid
              color={theme.colors.primary}
              containerStyle={styles.selectorIcon}
            />
          </View>
        </TouchableOpacity>
        <Modal
          visible={isYearPickerVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setYearPickerVisible(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.pickerContainer}>
              <View style={styles.pickerHeader}>
                <Text h4>{t('selectYear')}</Text>
                <TouchableOpacity onPress={() => setYearPickerVisible(false)}>
                  <Icon name="close" size={24} color={theme.colors.grey3} />
                </TouchableOpacity>
              </View>
              <YearPicker
                selectedYear={selectedYear}
                onYearChange={year => {
                  setSelectedYear(year)
                  setYearPickerVisible(false)
                }}
                yearsList={years}
              />
            </View>
          </View>
        </Modal>
        <SectionList
          sections={sections}
          renderSectionHeader={renderSectionHeader}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.type}-${index}`}
          stickySectionHeadersEnabled={false}
          contentContainerStyle={styles.listContent}
        />
      </View>
    </SafeAreaView>
  )
}

interface PerformanceReportListItemProps {
  companyId: string
  quarter: number
  year: number
  type: 'team' | 'store'
  onPressReportUrl: (url: string) => void
}

const PerformanceReportListItem = ({
  companyId,
  quarter,
  year,
  type,
  onPressReportUrl,
}: PerformanceReportListItemProps) => {
  const { theme } = useTheme()
  const styles = useStyles()
  const { data: reports, isLoading } = useQuarterlyReports({
    companyId,
    quarter,
    year,
    teamInsightsReport: true,
    storeInsightsReport: true,
  })

  const url =
    type === 'team'
      ? reports?.teamInsightsReportUrl
      : reports?.storeInsightsReportUrl

  return (
    <TouchableOpacity
      style={styles.itemContainer}
      onPress={() => {
        if (url) {
          onPressReportUrl(url)
        }
      }}
      disabled={!url}
    >
      <Text style={styles.itemText}>
        {type === 'team' ? t('teamInsightsReport') : t('storeInsightsReport')}
      </Text>
      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : (
        <Text style={[styles.itemStatus, !url && styles.unavailable]}>
          {url ? t('view') : t('unavailable')}
        </Text>
      )}
    </TouchableOpacity>
  )
}

const YearPicker = ({
  selectedYear,
  onYearChange,
  yearsList,
}: {
  selectedYear: number
  onYearChange: (year: number) => void
  yearsList: number[]
}) => {
  const styles = useStyles()
  if (Platform.OS === 'web') {
    return (
      <FlatList
        data={yearsList}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[
              styles.yearItem,
              selectedYear === item && styles.selectedYearItem,
            ]}
            onPress={() => onYearChange(item)}
          >
            <Text
              style={[
                styles.yearItemText,
                selectedYear === item && styles.selectedYearItemText,
              ]}
            >
              {item}
            </Text>
          </TouchableOpacity>
        )}
        keyExtractor={item => item.toString()}
        showsVerticalScrollIndicator={true}
        style={styles.yearList}
      />
    )
  }

  return (
    <WheelPicker
      data={yearsList.map(year => ({
        value: year,
        label: year.toString(),
      }))}
      value={selectedYear}
      onValueChanged={({ item: { value } }) => onYearChange(value)}
      itemTextStyle={styles.pickerItemText}
      overlayItemStyle={styles.wheelPickerItemContainer}
      visibleItemCount={5}
    />
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  yearSelector: {
    padding: 16,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey5,
  },
  sectionHeader: {
    padding: 16,
    backgroundColor: theme.colors.background,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey5,
  },
  listContent: {
    paddingBottom: 16,
    flexGrow: 1,
  },
  itemContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey5,
    backgroundColor: theme.colors.white,
  },
  itemText: {
    fontSize: 16,
    color: theme.colors.black,
  },
  itemStatus: {
    color: theme.colors.primary,
    fontWeight: 'bold',
  },
  unavailable: {
    color: theme.colors.grey3,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pickerContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: 10,
    width: '80%',
    padding: 20,
  },
  pickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  pickerItemText: {
    fontSize: 16,
    color: theme.colors.black,
  },
  wheelPickerItemContainer: {
    backgroundColor: 'gray',
  },
  selectorIcon: {
    marginLeft: 8,
  },
  yearSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  yearList: {
    maxHeight: 200,
  },
  yearItem: {
    padding: 12,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey5,
  },
  selectedYearItem: {
    backgroundColor: theme.colors.primary,
  },
  yearItemText: {
    fontSize: 16,
    color: theme.colors.black,
  },
  selectedYearItemText: {
    color: theme.colors.white,
  },
  companySelector: {
    marginTop: 10,
    alignItems: 'center',
  },
  companySelectorTitle: {
    fontSize: 24,
    color: theme.colors.black,
  },
}))
